/**
 * Typographic variants the same text arrives in from different writers (the client, Salesforce, an
 * admin, the AI), each with its plain form. Applied in order: entities first, so a decoded `&quot;`
 * folds like a typed quote, and whitespace last, after the bullets matched their line start. Same
 * table as the creatives app's `CreativeInstructionsCards`, so both show the same cards.
 */
const TYPOGRAPHY_FOLDS: ReadonlyArray<[RegExp, string]> = [
  // HTML entities echoed from an HTML-escaped prompt or email.
  [/&quot;/g, '"'],
  [/&#(39|x27);/g, '\''],
  [/&amp;/g, '&'],
  // Curly, low and reversed quotes, and guillemets.
  [/[“”„‟«»]/g, '"'],
  [/[‘’‚‛‹›]/g, '\''],
  // En/em dashes, the ellipsis, and bullets at the start of a line.
  [/[–—]/g, '-'],
  [/…/g, '...'],
  [/^[ \t]*[•*][ \t]*/gm, '- '],
  [/\s+/g, ' '],
  // The space next to a quote: French puts one inside guillemets, German none.
  [/ ?" ?/g, '"'],
  [/ ?' ?/g, '\''],
]

/**
 * `String.prototype.trim` plus U+0085 (NEL): the app trims with Dart's `trim()`, which strips it, JS's does not,
 * and neither language's `\s` matches it. Built from a string so the control character stays visible here.
 */
const EDGE_WHITESPACE = new RegExp('^[\\s\\u0085]+|[\\s\\u0085]+$', 'g')
export const trimLikeApp = (text: string): string => text.replace(EDGE_WHITESPACE, '')

/** `text` with the typography folded, so texts that differ only in typography compare equal. */
export const comparisonKey = (text: string): string =>
  TYPOGRAPHY_FOLDS.reduce((key, [pattern, plain]) => key.replace(pattern, plain), text)

/**
 * The assignment's own comments for the creative, trimmed, when they say more than `shownTexts`
 * (the texts already on screen), otherwise null.
 *
 * At order creation the platform composes these comments from the organization's comments and the
 * client's order comment, so they add nothing. A Salesforce WOLI sync or an admin edit replaces them,
 * and the AI summary is then built from the new text; with the summary hidden, this is the only place
 * that text reaches the creative. Every shown text is cut out of the comments, longest first so a short
 * one cannot break up a longer one that contains it, and the comments are shown only if something is
 * left. Known limit: the comments are a copy made at order creation, so an organization or order
 * comment changed later makes an unedited order show them too, quoting the old text.
 */
export const additionalComments = (comments: string | null | undefined, shownTexts: ReadonlyArray<string | null | undefined>): string | null => {
  const text = comments ? trimLikeApp(comments) : ''
  if (!text) return null

  const keys = shownTexts
    .map(shown => comparisonKey(trimLikeApp(shown ?? '')))
    .filter(key => key !== '')
    .sort((a, b) => b.length - a.length)

  // Cut to a space, not to nothing, so the pieces on either side of a cut cannot join up into
  // another shown text. A string pattern replaces its first occurrence literally.
  const rest = keys.reduce((remaining, key) => remaining.replace(key, ' '), comparisonKey(text))

  return trimLikeApp(rest) ? text : null
}
